import {
  Button,
  Image,
  Input,
  InputDropdown,
  Text,
  type DropdownOption,
} from '@kubit-ui-web/react-components';
import {
  BUILT_IN_DECKS,
  DEFAULT_DECK_CARDS,
  DEFAULT_DECK_ID,
  MIN_DECK_CARDS,
  estimateCards,
  isValidRoomId,
  normalizeDeckCards,
  type Deck,
  type DeckId,
} from '@point-ker/shared';
import { useState, type FormEvent } from 'react';

import { useSocket } from '@/socket/SocketProvider';
import type { UseRoom } from '@/socket/useRoom';
import { Card, CardGrid, Columns, PlainImage, Row, Stack } from '@/ui/layout';
import { HostLogin } from '@/screens/HostLogin';

/**
 * Landing screen: join an existing room, or create one if signed in.
 *
 * Joining comes first and is always available, because that is what most
 * people arriving here need — usually from a shared link that already filled
 * the code in for them.
 */
export function HomeScreen({ room, initialRoomId }: { room: UseRoom; initialRoomId: string }) {
  const { isSignedIn } = useSocket();
  const [error, setError] = useState<string | null>(null);

  return (
    <Stack $gap="1.5rem">
      <Stack $gap="0.25rem">
        {/* The SVG rather than the 512px PNG: it is a flat two-colour mark, so
            vector costs a fifth of the bytes and stays crisp at any size.
            Eager, not lazy — it is the first thing on the page. */}
        <Row>
          <PlainImage>
            <Image
              images={{ DEFAULT: { src: '/logo.svg' } }}
              alt="point-ker"
              width={64}
              height={64}
              loading="eager"
            />
          </PlainImage>
          <Text variant="MAIN_HEADING_H1_EXPANDED" component="h1">
            Public Scrums
          </Text>
        </Row>
        <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="p">
          Planning poker for Scrum teams. Create a room, share the code, estimate together.
        </Text>
      </Stack>

      {error !== null && (
        <Text variant="PARAGRAPH_SMALL_EXPANDED" component="p" color="#c00">
          {error}
        </Text>
      )}

      <Columns>
        <JoinRoomForm room={room} initialRoomId={initialRoomId} onError={setError} />
        <Stack $gap="1.5rem">
          <HostLogin />
          {isSignedIn && <CreateRoomForm room={room} onError={setError} />}
        </Stack>
      </Columns>
    </Stack>
  );
}

function JoinRoomForm({
  room,
  initialRoomId,
  onError,
}: {
  room: UseRoom;
  initialRoomId: string;
  onError: (message: string | null) => void;
}) {
  const [roomId, setRoomId] = useState(initialRoomId);
  const [name, setName] = useState('');
  const [asSpectator, setAsSpectator] = useState(false);
  const [busy, setBusy] = useState(false);

  const code = roomId.trim().toUpperCase();
  const canSubmit = isValidRoomId(code) && name.trim() !== '' && !busy;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    setBusy(true);
    room
      .joinRoom(code, name.trim(), asSpectator)
      .catch((reason: Error) => onError(reason.message))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <form onSubmit={submit}>
        <Stack>
          <Text variant="HEADING_H3_EXPANDED" component="h2">
            Join a room
          </Text>

          <Input
            variant="DEFAULT"
            id="join-code"
            label={{ content: 'Room code' }}
            placeholder="ABC234"
            value={roomId}
            maxLength={6}
            // Codes are shown in uppercase everywhere, so typing lowercase
            // should just work rather than failing validation.
            onChange={(event) => setRoomId(event.target.value.toUpperCase())}
          />
          <Input
            variant="DEFAULT"
            id="join-name"
            label={{ content: 'Your name' }}
            placeholder="Ada Lovelace"
            value={name}
            maxLength={32}
            onChange={(event) => setName(event.target.value)}
          />

          <Row>
            <Button
              variant={asSpectator ? 'PRIMARY' : 'SECONDARY'}
              size="SMALL"
              onClick={() => setAsSpectator((value) => !value)}
            >
              {asSpectator ? 'Joining as spectator' : 'Join as spectator'}
            </Button>
            <Text variant="PARAGRAPH_CAPTION_EXPANDED" component="span">
              Spectators watch without voting.
            </Text>
          </Row>

          <Row>
            <Button variant="PRIMARY" size="LARGE" type="submit" disabled={!canSubmit} loading={busy}>
              Join
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * Every card the deck could stop at, labelled by the card itself. The option's
 * value is how many cards that leaves, which is what `createRoom` sends.
 *
 * Starts at `MIN_DECK_CARDS`, so the first card in the scale is never offered
 * as a limit: a deck of one estimate makes every round unanimous.
 */
function cardLimitOptions(deck: Deck): DropdownOption[] {
  return estimateCards(deck)
    .slice(MIN_DECK_CARDS - 1)
    .map((card, index) => ({ value: String(index + MIN_DECK_CARDS), label: card.label }));
}

function CreateRoomForm({
  room,
  onError,
}: {
  room: UseRoom;
  onError: (message: string | null) => void;
}) {
  const [roomName, setRoomName] = useState('');
  const [hostName, setHostName] = useState('');
  const [deckId, setDeckId] = useState<DeckId>(DEFAULT_DECK_ID);
  const [requestedCards, setRequestedCards] = useState<number>(DEFAULT_DECK_CARDS);
  const [busy, setBusy] = useState(false);

  // Every deck has its own ceiling — Fibonacci runs to 89, t-shirt stops at
  // XXL — so the request is clamped for display rather than rewritten when the
  // deck changes. Holding the raw number means switching to t-shirt and back
  // returns the count the host actually picked, instead of the one t-shirt
  // could fit.
  const deck = BUILT_IN_DECKS[deckId as Exclude<DeckId, 'custom'>];
  const available = estimateCards(deck).length;
  const maxCards = normalizeDeckCards(requestedCards, available);

  const canSubmit = roomName.trim() !== '' && hostName.trim() !== '' && !busy;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onError(null);
    setBusy(true);
    room
      .createRoom({
        roomName: roomName.trim(),
        hostName: hostName.trim(),
        deckId,
        maxCards,
        hostAsSpectator: true,
      })
      .catch((reason: Error) => onError(reason.message))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <form onSubmit={submit}>
        <Stack>
          <Text variant="HEADING_H3_EXPANDED" component="h2">
            Create a room
          </Text>

          <Input
            variant="DEFAULT"
            id="create-room-name"
            label={{ content: 'Room name' }}
            placeholder="Sprint 42 planning"
            value={roomName}
            maxLength={60}
            onChange={(event) => setRoomName(event.target.value)}
          />
          <Input
            variant="DEFAULT"
            id="create-host-name"
            label={{ content: 'Your name' }}
            value={hostName}
            maxLength={32}
            onChange={(event) => setHostName(event.target.value)}
          />

          <Text variant="PARAGRAPH_MEDIUM_EXPANDED" component="p">
            Deck
          </Text>
          {/* Buttons rather than a dropdown: four options are quicker to pick
              than to open, and the selection stays visible while typing. */}
          <CardGrid>
            {Object.values(BUILT_IN_DECKS).map((deck) => (
              <Button
                key={deck.id}
                variant={deck.id === deckId ? 'PRIMARY' : 'SECONDARY'}
                size="SMALL"
                onClick={() => setDeckId(deck.id)}
              >
                {deck.name}
              </Button>
            ))}
          </CardGrid>

          {/* The host picks the card the deck stops at, not how many cards
              that is: "up to 21" is a decision about the estimates on offer,
              whereas "7 cards" is an implementation detail they would have to
              count out. The count is what travels on the wire, so the option's
              value carries it while its label shows the card. */}
          <InputDropdown
            variant="DEFAULT"
            id="create-card-limit"
            name="cardLimit"
            label={{ content: 'Max. Vote' }}
            value={String(maxCards)}
            elementsToShow={6}
            optionList={{ options: cardLimitOptions(deck) }}
            onChange={(event) => setRequestedCards(Number(event.target.value))}
          />

          <Row>
            <Button variant="PRIMARY" size="LARGE" type="submit" disabled={!canSubmit} loading={busy}>
              Create room
            </Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
